/**
 * EstimatePortal (Phase P1) — the PUBLIC, token-authorized customer estimate
 * page. Rendered OUTSIDE the app's auth wrapper (App.tsx routes to it before the
 * login gate) so customers open it from an emailed link with no account.
 *
 * Authorization is the token in the URL; this page never sends a JWT. Numbers
 * come from the backend, which computes them with the same engine as the app —
 * so the portal always matches the on-screen / invoice / email totals.
 *
 * Built for Long Distance now; the payload carries portalType/moveType so a
 * future Local variant can branch here without a rewrite.
 */

import React, { useEffect, useRef, useState } from 'react';
import { formatPhone, telHref } from '../lib/format';
import { isPrintMode, isStaffPreview, downloadPortalPdf, DownloadState } from './portal/downloadPdf';
import { formatMoney, formatDate } from '../lib/utils';
import { Icons } from '../lib/icons';
import { usePortalReady } from './portal/portalReady';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';

// Brand assets (from balticvanlines.ca).
// SELF-HOSTED, same origin as the portal. It used to be hotlinked from
// balticvanlines.ca and rendered as alt text inside the PDF on Railway — while
// loading fine from a browser and from curl, and fine in headless Chromium
// locally (300x178). That combination means the failure was specific to the
// container's egress, not to the URL, the format, or CORS.
//
// Rather than keep guessing, the dependency is removed: a client's invoice
// should not require a third-party WordPress host to be reachable at the moment
// the PDF is generated. Same-origin also means it is already warm in the cache
// the portal was just served from.
const TAGLINE = 'Your Best Move Starts Here';
const PHONE = '647 931 5244';
const GOOGLE_REVIEWS_URL = 'https://g.page/r/CUyPcsvfbQCfEAE/';

// Owner's terms text. Reworded 2026-10 for plain English, same meaning (Daryl's
// wording); the PDF's Terms use the same text.
//
// The deposit term sits at the foot of the page, just above Accept, where the
// decision it belongs to is made. The rest are "Good to know", always open,
// below the breakdown. The old "dedicated
// coordinator" paragraph is gone: the sixth "Everything included" card says it.
const DEPOSIT_TERM = {
  heading: 'Securing your space & rate',
  body: 'A non-refundable deposit of 10% (minimum $300) secures your space and rate. This deposit can be made by Visa, Mastercard, or Email Transfer. If you need to make changes, please let us know as soon as possible so we can accommodate.',
};
/**
 * LOCAL DEPOSIT (056): a flat amount — the estimate's own, else the tariff's
 * default, else $150 (the server resolves it). The card and the PDF term say
 * the same thing.
 */
const localDepositTerm = (amount: number) => ({
  heading: 'Securing your move date',
  body: `A non-refundable deposit of ${wholeMoney(amount)} secures your move date. Visa, Mastercard or Email Transfer.`,
});
const GOOD_TO_KNOW: { heading: string; body: string; weight?: boolean }[] = [
  {
    heading: 'How your final cost is determined',
    weight: true,
    body: 'This estimate is based on the information you’ve shared through the online portal or with your moving consultant. Your final cost is set at the end of your move, based on the actual weight transported.',
  },
  {
    heading: 'About the weight range',
    weight: true,
    body: 'Household weights can vary. The weight shown is a non-binding guideline to give you a sense of what we’ll be transporting.',
  },
  {
    heading: 'Dates, rates & validity',
    body: 'Dates are based on availability at the time of your inquiry, and dates and rates may change. This estimate is valid for 72 hours. After that, please check with your consultant that nothing has changed.',
  },
];

/**
 * THE PDF's TERMS — Good to know's own wording (GOOD_TO_KNOW, above), then the
 * deposit, condensed to one line for the small-print block.
 */
const PRINT_DEPOSIT = 'A non-refundable deposit of 10% (minimum $300) secures your space and rate, payable by Visa, Mastercard or Email Transfer.';

/**
 * LOCAL moves are billed by the hour: weight plays no part. These bullets take
 * the place of the weight-based text — the two weight items in "Good to know" on
 * the page, and the "Final cost" and "Weight range" terms in the PDF.
 */
const LOCAL_BILLING: string[] = [
  'Your time on site is billed in 15-minute steps, from arrival to completion.',
  'Travel time is a flat charge, dock to dock, set in advance.',
  'Travel time counts toward your minimum hours.',
  'Estimated hours are a guideline. Your final cost is based on the actual time on site.',
  'Fuel, admin, storage and packing materials are separate and shown on your estimate.',
  'There are no extra charges for heavy items, stairs or elevators.',
  'Payment will be processed with the crew on site, before move completion.',
];

/** Under the six cards, inside the "Everything included" box. Three each. */
const TRUST: { text: string; only?: 'ld' | 'local' }[] = [
  { text: 'Licensed and insured' },
  { text: 'Background-checked crews' },
  { text: 'Full value protection on request', only: 'ld' },
  { text: 'Crew training program', only: 'local' },
];

/**
 * "Everything included" — six cards each, LD and local, so the fixed grid is
 * always 3 + 3 or 2 + 2 + 2. Long distance first, then local (hourly) below.
 */
type IncludedCard = { icon: keyof typeof Icons; title: string; line: string; panel: string };
// The one card both lists share, word for word.
const PROTECTED: IncludedCard = { icon: 'wrapped', title: 'Professionally Protected', line: 'Every piece, wrapped with care',
  panel: 'Every piece of furniture is wrapped in moving blankets, shrink wrap and tape before it leaves your home.' };
const INCLUDED: IncludedCard[] = [
  { icon: 'roomToRoom', title: 'Room to Room Service', line: 'From wherever it is to wherever you want it',
    panel: 'We don’t leave everything by the door. You tell us where it goes, and we get it there for you.' },
  { icon: 'tools', title: 'Disassembly & Assembly', line: 'Beds, tables and more, taken apart and rebuilt',
    panel: 'Our crew takes apart and reassembles the furniture throughout your home. You pack the boxes, we do the rest.' },
  { icon: 'loading', title: 'Loading & Unloading Included', line: 'Our crew does the heavy lifting',
    panel: 'All the labour is included in your price. Our crew handles everything from start to finish, so you don’t need to lift a thing.' },
  PROTECTED,
  { icon: 'coverage', title: '$1.00/lb Coverage, Free', line: '67% higher than the industry standard',
    panel: 'Your belongings are covered at $1.00 per pound at no extra cost, 67% more than the industry standard of $0.60/lb. Want full value protection? Just ask your consultant.' },
  { icon: 'coordinator', title: 'One Coordinator, Start to Finish', line: 'The same person from your first call to your last box',
    panel: 'We keep your move personal. The same consultant looks after you from your first call to delivery day, with the rest of our team ready to help whenever you need it.' },
];

const INCLUDED_LOCAL: IncludedCard[] = [
  { icon: 'hourly', title: 'All-Inclusive Hourly Rate', line: 'We don’t charge extra for heavy items, stairs or elevators',
    panel: 'Your hourly rate covers the crew, the truck, and the equipment needed to complete your move. Some things, such as fuel, admin, storage and packing materials, are separate.' },
  { icon: 'route', title: 'Your Move, Your Way', line: 'Our crew is at your disposal',
    panel: 'You need it done, we will get it done. Just point and tell us what needs to happen.' },
  { icon: 'tools', title: 'Disassembly & Assembly', line: 'Our crews are equipped with the tools to take apart and rebuild your furniture',
    panel: 'Our knowledgeable crews are all equipped with the tools they need to efficiently help you take apart and later put back together your furniture.' },
  PROTECTED,
  { icon: 'friendly', title: 'Efficient, Friendly Crew', line: 'Making a stressful day easier',
    panel: 'Our team isn’t just trained and background checked. We also make sure to bring on hardworking, friendly and honest workers, making a day that is always stressful a bit easier.' },
  { icon: 'box', title: 'Packing Available', line: 'Sometimes you need an extra hand',
    panel: 'Not able to get everything packed in time? Let your consultant know and our crew can help you get it done.' },
];

// ---- small presentational helpers ------------------------------------------

function accessLine(a: { stairs: number; walkFt: number; elevator: boolean; shuttle: boolean }): string {
  const bits: string[] = [];
  if (a.stairs > 0) bits.push(`${a.stairs} stairs`);
  if (a.walkFt > 0) bits.push(`${a.walkFt} ft walk`);
  if (a.elevator) bits.push('elevator');
  if (a.shuttle) bits.push('shuttle');
  return bits.length ? bits.join(' · ') : 'No special access noted';
}

function windowLabel(start: string | null, end: string | null, arrival: string | null): string {
  if (start || end) return `${start ? formatDate(start) : '—'} – ${end ? formatDate(end) : '—'}`;
  return arrival || '—';
}

/** "$189", or "$189.50" when there are cents. */
const wholeMoney = (n: unknown): string => {
  const v = Number(n) || 0;
  return Number.isInteger(v) ? `$${v.toLocaleString('en-CA')}` : formatMoney(v);
};

/**
 * LOCAL LABOUR LINE (053): the hourly base line once hours are recorded (the
 * engine gives it the 'hr' unit then). Without hours it is today's flat line.
 */
const isLabour = (it: any, ld: boolean): boolean => !ld && it.source === 'base' && it.unitLabel === 'hr';
const LABOUR_NOTE = 'Estimated time, includes flat dock-to-dock travel';
const plural = (n: number, one: string) => `${n.toLocaleString('en-CA')} ${one}${n === 1 ? '' : 's'}`;

/**
 * The phone line's small second line: "$1.10/lb × 6,000 lb", "$75.00/flight × 1",
 * "12%". Same figures as the table's Qty and Rate columns (lineQtyRate). A
 * measured unit (lb, cuft, hr) follows the quantity; a per-item one ("flight")
 * is already in the rate. '' when the line has no rate/qty detail (a flat base
 * line, whose only figure is its amount).
 */
const MEASURED_UNITS = new Set(['lb', 'cuft', 'hr']);
function lineDetail(it: any, labour = false): string {
  const { qty, rate } = lineQtyRate(it, labour);
  if (labour) return `${rate} × ${qty}`;
  if (it.rateKind === 'pct') return rate;
  if (qty === '—') return '';
  const unit = it.rateKind === 'unit' && MEASURED_UNITS.has(it.unitLabel) ? ` ${it.unitLabel}` : '';
  return `${rate} × ${qty}${unit}`;
}

function lineQtyRate(it: any, labour = false): { qty: string; rate: string } {
  // LOCAL LABOUR (053): "5.5 hr" at "$189/hr" — the billed hours, travel included.
  if (labour) return { qty: `${Number(it.quantity).toLocaleString()} hr`, rate: `${wholeMoney(it.rate)}/hr` };
  const qty = it.rateKind === 'pct' || (it.source === 'base' && !it.unitLabel) ? '—' : Number(it.quantity).toLocaleString();
  const rate =
    it.rateKind === 'pct'
      ? `${Number(it.rate)}%`
      : it.rateKind === 'unit'
        ? `${formatMoney(it.rate)}/${it.unitLabel ?? 'unit'}`
        : formatMoney(it.rate);
  return { qty, rate };
}

/**
 * The six "Everything included" cards. Each card's "i" opens a panel that spans
 * the grid directly under that card's row (a full-width grid item placed after
 * the row's last card), with a caret over the card. One panel at a time; the
 * same "i" or Escape closes it. The column count mirrors the CSS breakpoint so
 * the panel lands under the right row at 3 columns and at 2.
 */
const INCL_WIDE = '(min-width:900px)';
function IncludedGrid({ cards }: { cards: IncludedCard[] }) {
  const [open, setOpen] = useState<number | null>(null);
  const [cols, setCols] = useState(() => (window.matchMedia(INCL_WIDE).matches ? 3 : 2));
  const btns = useRef<(HTMLButtonElement | null)[]>([]);

  useEffect(() => {
    const mq = window.matchMedia(INCL_WIDE);
    const on = () => setCols(mq.matches ? 3 : 2);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);

  useEffect(() => {
    if (open === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      btns.current[open]?.focus();
      setOpen(null);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  const rows = Math.ceil(cards.length / cols);
  const row = open === null ? -1 : Math.floor(open / cols);
  const after = open === null ? cards.length - 1 : Math.min((row + 1) * cols, cards.length) - 1;
  // Card rows stay equal height (1fr); the panel's row is sized to its text.
  const tracks = Array.from({ length: rows }, (_, r) => (r === row ? '1fr auto' : '1fr')).join(' ');

  const panel = (
    <div
      key={`panel-${row}`}
      id="bvl-incl-panel"
      className="bvl-incl-panel"
      role="region"
      aria-labelledby={open === null ? undefined : `bvl-incl-t${open}`}
      hidden={open === null}
      style={open === null ? undefined : ({ '--cols': cols, '--col': open % cols } as React.CSSProperties)}
    >
      {open !== null && cards[open].panel}
    </div>
  );

  return (
    <div className="bvl-incl" style={open === null ? undefined : { gridTemplateRows: tracks }}>
      {cards.map((c, i) => {
        const Icon = Icons[c.icon];
        const active = open === i;
        return (
          <React.Fragment key={c.title}>
            <div className={active ? 'bvl-incl-card bvl-incl-active' : 'bvl-incl-card'}>
              <div className="bvl-incl-icon"><Icon size={22} aria-hidden /></div>
              <div className="bvl-incl-title" id={`bvl-incl-t${i}`}>{c.title}</div>
              <div className="bvl-incl-line">{c.line}</div>
              <button
                type="button"
                ref={(el) => { btns.current[i] = el; }}
                className="bvl-incl-info"
                aria-expanded={active}
                aria-controls="bvl-incl-panel"
                aria-label={`More about ${c.title}`}
                onClick={() => setOpen(active ? null : i)}
              >
                i
              </button>
            </div>
            {i === after && panel}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ============================================================================

export function EstimatePortal({ token }: { token: string }) {
  const [data, setData] = useState<any | null>(null);
  const [status, setStatus] = useState<'loading' | 'ok' | 'error'>('loading');
  const [accepted, setAccepted] = useState(false);
  // The PDF error surfaces BESIDE the button, never as an alert() — a client on a
  // phone dismisses an alert and the reason goes with it.
  const [pdf, setPdf] = useState<DownloadState>({ busy: false, error: null });
  const [accepting, setAccepting] = useState(false);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/portal/estimate/${encodeURIComponent(token)}${isStaffPreview() ? '?preview=1' : ''}`);
        if (!res.ok) throw new Error('not found');
        const body = await res.json();
        if (cancelled) return;
        setData(body.data);
        setAccepted(!!body.data.acceptedAt);
        setStatus('ok');
      } catch {
        if (!cancelled) setStatus('error');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const handleAccept = async () => {
    if (isStaffPreview()) return;   // a rep previewing never accepts for the client
    setAccepting(true);
    try {
      const res = await fetch(`${API_BASE_URL}/portal/estimate/${encodeURIComponent(token)}/accept`, { method: 'POST' });
      if (!res.ok) throw new Error('accept failed');
      setAccepted(true);
      setShowModal(true);
    } catch {
      alert('Sorry — something went wrong recording your approval. Please call us at ' + formatPhone(officePhone) + '.');
    } finally {
      setAccepting(false);
    }
  };

  // P4 fix — see DocumentPortal: the renderer waits for this rather than
  // guessing how long React takes.
  usePortalReady(status === 'loading' ? 'loading' : status === 'ok' ? 'ok' : 'error');

  const officePhone = data?.company?.phone || PHONE;
  const ld = !!data?.isLongDistance;
  // Local: the flat deposit (056). Long distance keeps DEPOSIT_TERM.
  const depositTerm = ld ? null : localDepositTerm(Number(data?.deposit?.amount ?? 150));
  // The PDF (print=1): plain, short, one page where it can be. No welcome, no
  // cards, no trust items; the terms condensed into one block. See .bvl-print.
  const print = isPrintMode();
  // "What's included" — long distance only, from Settings (the server sends
  // null on a local move).
  const included: string[] = ld && Array.isArray(data?.includedLd) ? data.includedLd.filter((p: unknown) => String(p ?? '').trim()) : [];
  const preview = !print && isStaffPreview();
  const trust = (cls: string) => (
    <div className={cls}>
      {TRUST.filter((t) => !t.only || t.only === (ld ? 'ld' : 'local')).map((t) => (
        <div key={t.text} className="bvl-trust-item"><Icons.check size={14} aria-hidden /> {t.text}</div>
      ))}
    </div>
  );
  // "3 movers · 1 truck" (053, local) — whichever is recorded; '' when neither.
  const crew = ld ? '' : [
    data?.hourly?.movers ? plural(Number(data.hourly.movers), 'mover') : '',
    data?.hourly?.trucks ? plural(Number(data.hourly.trucks), 'truck') : '',
  ].filter(Boolean).join(' · ');
  const firstName = String(data?.customer?.firstName ?? '').trim();
  const repFirst = String(data?.rep?.firstName ?? '').trim();
  const jobNo = String(data?.jobNumber ?? '').trim();
  const consultant = String(data?.rep?.name ?? '').trim() || `${data?.company?.name || 'Baltic Van Lines'} team`;
  const route = data?.route?.from && data?.route?.to ? ` from ${data.route.from} to ${data.route.to}` : '';

  return (
    <div className={print ? 'bvl-portal bvl-print' : 'bvl-portal'}>
      <style>{CSS}</style>

      {/* Staff preview only (the CRM's "Preview portal"): one slim line, so the
          rep knows Accept will not work here. The client never sees it. */}
      {preview && (
        <div style={{ background: '#1f2937', color: '#fff', fontSize: '13px', textAlign: 'center', padding: '7px 12px' }}>
          Staff preview — exactly what the client sees. Accept and Submit Inventory are disabled here.
        </div>
      )}

      {/* Header (shown on every state) */}
      <header className="bvl-header">
        <div className="bvl-header-inner">
          {/* White on the navy bar; the PDF's header is white, so the colour version. */}
          <img src={print ? '/brand/logo-horizontal-notag-color.svg' : '/brand/logo-horizontal-notag-white.svg'} alt="Baltic Van Lines" className="bvl-logo" />
          {print && data ? (
            /* PDF: the job number is the headline — the phone system asks for it. */
            <div className="bvl-print-hdr">
              {jobNo && <div className="bvl-print-job">Job #{jobNo}</div>}
              <div className="bvl-print-contact">Consultant: {consultant} · {formatPhone(officePhone)}</div>
            </div>
          ) : (
          <div className="bvl-header-right">
            <div className="bvl-tagline">{TAGLINE}</div>
            {/* The office line from Settings → Company → Phone once the estimate
                has loaded; the constant until then (and on the error screen).
                The job number beside it, as loud as the phone: our phone system
                asks callers for it. */}
            <div className="bvl-hdr-line">
              <a href={telHref(officePhone)} className="bvl-phone"><Icons.phone size={14} style={{ verticalAlign: "text-bottom" }} /> {formatPhone(officePhone)}</a>
              {jobNo && <span className="bvl-hdr-job">Job #{jobNo}</span>}
            </div>
          </div>
          )}
        </div>
      </header>

      {status === 'loading' && <div className="bvl-center">Loading your estimate…</div>}

      {status === 'error' && (
        <div className="bvl-center bvl-fallback">
          <h2>This estimate link is no longer available</h2>
          <p>Please contact us and we’ll be glad to help.</p>
          <a href={`tel:${PHONE.replace(/\s/g, '')}`} className="bvl-btn bvl-btn-amber">Call {PHONE}</a>
        </div>
      )}

      {status === 'ok' && data && (
        <main className="bvl-main">
          {/* Welcome — a person talking, not a document title. Under it, the
              one strip a caller needs: who to ask for, the number, and the job
              number the phone system will ask for. */}
          {!print && <section className="bvl-welcome">
            <h1>{firstName ? `${firstName}, here’s your moving plan` : 'Here’s your moving plan'}</h1>
            <p className="bvl-intro">
              We’ve put this together around your move{route}. Have a look, and if anything needs adjusting,{' '}
              {repFirst || 'our team'} is just a call or reply away.
            </p>
            <div className="bvl-contact">
              <span className="bvl-contact-a">
                Your consultant: <span className="bvl-nowrap"><b>{consultant}</b> · <a href={telHref(officePhone)}>{formatPhone(officePhone)}</a></span>
              </span>
              {jobNo && (
                <span className="bvl-contact-b">
                  <span className="bvl-contact-sep"> · </span><b>Job #{jobNo}</b> (have it handy when you call)
                </span>
              )}
            </div>
          </section>}

          {!print && (
            <section className="bvl-card">
              <h2 className="bvl-section-h">{ld ? 'Everything included in your move' : 'Everything included in your hourly rate'}</h2>
              <IncludedGrid key={ld ? 'ld' : 'local'} cards={ld ? INCLUDED : INCLUDED_LOCAL} />
              {/* Same 3-column grid as the cards on wide screens, so each item
                  sits under a card column; one per line below 900px. */}
              {trust('bvl-trust-grid')}
            </section>
          )}

          {/* Client + move summary */}
          <section className="bvl-card">
            <h2 className="bvl-section-h">Your Move</h2>
            <div className="bvl-grid2">
              <div>
                <div className="bvl-label">Client</div>
                <div className="bvl-value">
                  {data.customer.firstName} {data.customer.lastName}
                  {data.customer.email ? <><br />{data.customer.email}</> : null}
                  {data.customer.phone ? <><br />{formatPhone(data.customer.phone)}</> : null}
                </div>
              </div>
              <div>
                <div className="bvl-label">Move summary</div>
                <div className="bvl-value">
                  {data.move.type || '—'}{data.move.size ? ` · ${data.move.size}` : ''}
                  {/* Binding is a weight term: long distance only. */}
                  {ld && (
                    <>
                      <br />
                      <span className={data.move.binding ? 'bvl-badge bvl-badge-amber' : 'bvl-badge'}>
                        {data.move.binding ? 'Binding' : 'Non-binding'}
                      </span>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* LD runs on pickup / delivery windows; a local move has a date. */}
            {ld ? (
              <div className="bvl-grid2">
                <div>
                  <div className="bvl-label">Pickup window</div>
                  <div className="bvl-value">{windowLabel(data.windows.pickupStart, data.windows.pickupEnd, data.windows.pickupArrivalWindow)}</div>
                </div>
                <div>
                  <div className="bvl-label">Delivery window</div>
                  <div className="bvl-value">{windowLabel(data.windows.dropoffStart, data.windows.dropoffEnd, data.windows.dropoffArrivalWindow)}</div>
                </div>
              </div>
            ) : (
              <div className="bvl-grid2">
                <div>
                  <div className="bvl-label">Move date</div>
                  {/* A calendar date (leads.move_date), not an instant: formatDate
                      reads it as that day wherever the client is, so no zone
                      can move it. */}
                  <div className="bvl-value">{formatDate(data.move.moveDate ? String(data.move.moveDate).slice(0, 10) : null)}</div>
                </div>
                {/* The crew and the minimum (053), each only when recorded. */}
                {crew && (
                  <div>
                    <div className="bvl-label">Your crew</div>
                    <div className="bvl-value">{crew}</div>
                  </div>
                )}
                {data.hourly?.minimumHours != null && (
                  <div>
                    <div className="bvl-label">Minimum hours</div>
                    <div className="bvl-value">
                      {plural(Number(data.hourly.minimumHours), 'hr')}
                      {data.hourly.minimumAmount != null && ` (${wholeMoney(data.hourly.minimumAmount)} + fees at minimum)`}
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="bvl-grid2">
              <div>
                <div className="bvl-label">Pickup (origin)</div>
                <div className="bvl-value">
                  {data.origin.address || '—'}
                  <div className="bvl-soft">{accessLine(data.origin)}</div>
                </div>
              </div>
              <div>
                <div className="bvl-label">Dropoff (destination)</div>
                <div className="bvl-value">
                  {data.destination.address || '—'}
                  <div className="bvl-soft">{accessLine(data.destination)}</div>
                </div>
              </div>
            </div>

            {/* Weights — long distance only; local is billed by the hour. */}
            {ld && <>
            <div className="bvl-weights">
              {data.weights.minimumWeight != null && (
                <div className="bvl-weight-cell">
                  <div className="bvl-weight-num">{Number(data.weights.minimumWeight).toLocaleString()} lbs</div>
                  <div className="bvl-weight-lbl">Minimum weight</div>
                </div>
              )}
              <div className="bvl-weight-cell">
                <div className="bvl-weight-num">{Number(data.weights.estimatedWeight).toLocaleString()} lbs</div>
                <div className="bvl-weight-lbl">Estimated weight</div>
              </div>
            </div>
            <p className="bvl-soft bvl-weight-note">
              Weights are a non-binding guideline; your final cost is adjusted by actual weight at the end of your move.
            </p>
            </>}
          </section>

          {/* Services breakdown */}
          <section className="bvl-card">
            <h2 className="bvl-section-h">Estimate Details</h2>
            <div className="bvl-table-wrap">
              <table className="bvl-table">
                <thead>
                  <tr>
                    <th>Description</th>
                    <th className="bvl-num">Qty</th>
                    <th className="bvl-num">Rate</th>
                    <th className="bvl-num">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.estimate.items ?? []).map((it: any, i: number) => {
                    const labour = isLabour(it, ld);
                    const { qty, rate } = lineQtyRate(it, labour);
                    return (
                      <tr key={i}>
                        <td>
                          {it.desc}
                          {labour && <div className="bvl-soft bvl-tiny">{LABOUR_NOTE}</div>}
                          {Number(it.discountAmt) > 0 && <div className="bvl-soft bvl-tiny">less discount {formatMoney(it.discountAmt)}</div>}
                        </td>
                        <td className="bvl-num">{qty}</td>
                        <td className="bvl-num">{rate}</td>
                        <td className="bvl-num">{formatMoney(it.net)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* PHONES (≤ 600px): the same lines, stacked — a four-column table
                cannot fit a phone without scrolling sideways, and nothing on this
                page scrolls sideways. CSS picks one of the two; the PDF
                (.bvl-print) always gets the table. */}
            <div className="bvl-lines">
              {(data.estimate.items ?? []).map((it: any, i: number) => {
                const labour = isLabour(it, ld);
                const detail = lineDetail(it, labour);
                return (
                  <div key={i} className="bvl-line">
                    <div className="bvl-line-top">
                      <span className="bvl-line-desc">{it.desc}</span>
                      <span className="bvl-line-amt">{formatMoney(it.net)}</span>
                    </div>
                    {labour && <div className="bvl-line-detail">{LABOUR_NOTE}</div>}
                    {detail && <div className="bvl-line-detail">{detail}</div>}
                    {Number(it.discountAmt) > 0 && <div className="bvl-line-detail">less discount {formatMoney(it.discountAmt)}</div>}
                  </div>
                );
              })}
            </div>

            <div className="bvl-totals">
              <div className="bvl-total-row"><span>Subtotal</span><span>{formatMoney(data.estimate.subtotal)}</span></div>
              {Number(data.estimate.totalDiscountAmt) > 0 && (
                <>
                  <div className="bvl-total-row"><span>Discount</span><span>−{formatMoney(data.estimate.totalDiscountAmt)}</span></div>
                  <div className="bvl-total-row"><span>Discounted subtotal</span><span>{formatMoney(data.estimate.discountedSubtotal)}</span></div>
                </>
              )}
              {/* Rate only — the BRANCH NAME used to be printed here ("Tax (ON 13%)").
                  Branches are named operationally, so a branch called "Toronto
                  Warehouse" or "BVL-2" would appear on a client's estimate. The
                  invoice portal already showed the rate alone; these now agree. */}
              <div className="bvl-total-row"><span>Tax ({Number(data.estimate.taxRate)}%)</span><span>{formatMoney(data.estimate.tax)}</span></div>
              <div className="bvl-total-row bvl-grand"><span>Estimated Total</span><span>{formatMoney(data.estimate.total)}</span></div>
            </div>

            {data.customerNotes && (
              <div className="bvl-notes">
                <div className="bvl-label">Notes</div>
                <div className="bvl-value" style={{ whiteSpace: 'pre-wrap' }}>{data.customerNotes}</div>
              </div>
            )}
          </section>

          {/* The PDF's terms: four in one small block, in place of Good to know
              and the deposit card. */}
          {print && (
            <section className="bvl-card bvl-print-terms">
              <h2 className="bvl-section-h">Terms</h2>
              {/* Local: the hourly bullets in place of the two weight terms. */}
              {!ld && (
                <>
                  <p><b>How you are billed.</b></p>
                  <ul className="bvl-print-bullets">
                    {LOCAL_BILLING.map((b) => <li key={b}>{b}</li>)}
                  </ul>
                </>
              )}
              {/* What's included (LD only, Settings → Documents): a heading and one bullet per row. */}
              {included.length > 0 && (
                <>
                  <p><b>What’s included.</b></p>
                  <ul className="bvl-print-bullets">
                    {included.map((p) => <li key={p}>{p}</li>)}
                  </ul>
                </>
              )}
              {GOOD_TO_KNOW.filter((t) => ld || !t.weight).map((t) => (
                <p key={t.heading}><b>{t.heading}.</b> {t.body}</p>
              ))}
              <p><b>Deposit.</b> {depositTerm ? depositTerm.body : PRINT_DEPOSIT}</p>
            </section>
          )}

          {/* Good to know — the owner's terms, always open (portal and staff preview). */}
          {!print && <section className="bvl-card bvl-gtk">
            <h2 className="bvl-gtk-title">Good to know</h2>
            <div className="bvl-gtk-body">
              {included.length > 0 && (
                <div className="bvl-gtk-item">
                  <div className="bvl-gtk-h">What’s included</div>
                  <ul className="bvl-gtk-list">
                    {included.map((p) => <li key={p}>{p}</li>)}
                  </ul>
                </div>
              )}
              {!ld && (
                <div className="bvl-gtk-item">
                  <div className="bvl-gtk-h">How your final cost is determined</div>
                  <ul className="bvl-gtk-list">
                    {LOCAL_BILLING.map((b) => <li key={b}>{b}</li>)}
                  </ul>
                </div>
              )}
              {GOOD_TO_KNOW.filter((t) => ld || !t.weight).map((t) => (
                <div key={t.heading} className="bvl-gtk-item">
                  <div className="bvl-gtk-h">{t.heading}</div>
                  <p>{t.body}</p>
                </div>
              ))}
            </div>
          </section>}

          {/* The deposit, where the decision is made: right above Accept. */}
          {!print && <section className="bvl-card bvl-deposit-card">
            <div className="bvl-term">
              <div className="bvl-term-h">{(depositTerm ?? DEPOSIT_TERM).heading}</div>
              <p>{(depositTerm ?? DEPOSIT_TERM).body}</p>
            </div>
          </section>}

          {/* Action buttons.
              HIDDEN WHEN print=1, in the render — the same guard the document
              portal's PortalActions applies, for the same reason: the PDF has no
              buttons. The CRM's "Preview portal" opens ?preview=1 instead, which
              shows this section but disables Accept (see `preview`), so a rep
              can never accept on the client's behalf. */}
          {!isPrintMode() && (
          <section className="bvl-actions">
            {accepted ? (
              <div className="bvl-accepted"><Icons.check size={18} style={{ verticalAlign: "text-bottom" }} /> Estimate Accepted</div>
            ) : (
              <button className="bvl-btn bvl-btn-amber bvl-btn-lg" onClick={handleAccept} disabled={accepting || preview}
                      title={preview ? 'Disabled in staff preview' : undefined}>
                {accepting ? 'Sending…' : 'Accept Estimate'}
              </button>
            )}

            {/* BOTH OF THESE SHIPPED MONTHS AGO. The inventory portal is P5 and the
                estimate PDF is P4, and this page was still telling clients to wait
                for a coordinator to guide them through something they could do in
                two clicks. The inventory link is minted server-side and arrives in
                the payload; it is absent only if that minting failed, in which case
                the button does not appear rather than opening nothing. */}
            {/* Staff preview: shown but disabled, like Accept — the link is the
                client's own inventory portal, where a rep could change the list. */}
            {data.inventoryUrl && (preview ? (
              <button className="bvl-btn bvl-btn-ghost" disabled title="Disabled in staff preview">
                Submit Inventory
              </button>
            ) : (
              <a className="bvl-btn bvl-btn-ghost" href={data.inventoryUrl} target="_blank" rel="noopener noreferrer">
                Submit Inventory
              </a>
            ))}
            <button
              className="bvl-btn bvl-btn-ghost"
              onClick={async () => {
                setPdf({ busy: true, error: null });
                const err = await downloadPortalPdf(`/portal/estimate/${token}/pdf`);
                setPdf({ busy: false, error: err });
              }}
              disabled={pdf.busy}
            >
              {pdf.busy ? 'Preparing…' : 'Download PDF Estimate'}
            </button>

            <a className="bvl-btn bvl-btn-reviews" href={GOOGLE_REVIEWS_URL} target="_blank" rel="noopener noreferrer">
              <span className="bvl-stars">{[0,1,2,3,4].map((i) => <Icons.starFilled key={i} size={16} />)}</span>
              See our Google Reviews
              <span className="bvl-soft bvl-tiny">Rated by our customers</span>
            </a>
            {pdf.error && <div className="bvl-pdf-error">{pdf.error}</div>}
          </section>
          )}

          {/* Footer */}
          {print ? (
            <footer className="bvl-footer">
              {[data.company.name, formatPhone(officePhone), data.company.email, `Job #${data.jobNumber}`].filter(Boolean).join(' · ')}
            </footer>
          ) : (
          <footer className="bvl-footer">
            <div>{data.company.name}</div>
            <div>
              <a href={telHref(officePhone)}>{formatPhone(officePhone)}</a>
              {data.company.email ? <> · {data.company.email}</> : null}
            </div>
            <div className="bvl-soft bvl-tiny">This estimate is valid for 72 hours.</div>
            <div className="bvl-soft bvl-tiny bvl-job">Job #{data.jobNumber}</div>
          </footer>
          )}
        </main>
      )}

      {/* Accept confirmation modal */}
      {showModal && (
        <div className="bvl-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="bvl-modal" onClick={(e) => e.stopPropagation()}>
            <div className="bvl-modal-check"><Icons.check size={30} /></div>
            <h3>Thank you!</h3>
            <p>Your moving consultant has been informed of your approval. Please speak with them to confirm your reservation with a deposit.</p>
            <button className="bvl-btn bvl-btn-amber" onClick={() => setShowModal(false)}>Got it</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ---- Styles (scoped under .bvl-portal; mobile-first + responsive) ----------
const CSS = `
.bvl-portal {
  --navy:#1a2b4a; --navy-deep:#16233d;
  --amber:#f5a623; --amber-deep:#e8951c;
  --bg:#f7f8fa; --bg-tint:#f4f5f7;
  --text:#1f2733; --text-soft:#5b6473; --line:#e3e6ec; --white:#ffffff;
  background:var(--bg); color:var(--text); min-height:100vh;
  font-family:-apple-system,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;
  line-height:1.55; -webkit-font-smoothing:antialiased;
}
.bvl-portal * { box-sizing:border-box; }
.bvl-header { background:var(--navy); color:var(--white); }
.bvl-header-inner { max-width:820px; margin:0 auto; padding:16px 20px; display:flex; align-items:center; justify-content:space-between; gap:16px; flex-wrap:wrap; }
.bvl-logo { height:40px; width:auto; display:block; }
.bvl-header-right { text-align:right; }
.bvl-tagline { font-size:13px; font-weight:600; letter-spacing:.3px; opacity:.92; }
.bvl-phone { display:inline-block; margin-top:2px; color:var(--amber); font-size:14px; font-weight:700; text-decoration:none; }
.bvl-main { max-width:820px; margin:0 auto; padding:24px 16px 60px; }
.bvl-center { max-width:520px; margin:60px auto; padding:0 20px; text-align:center; color:var(--text-soft); }
.bvl-fallback h2 { color:var(--navy); margin-bottom:8px; }
.bvl-fallback p { margin-bottom:20px; }
.bvl-welcome { padding:30px 4px 14px; }
.bvl-welcome h1 { font-size:30px; color:var(--navy); margin:0; font-weight:800; line-height:1.2; letter-spacing:-.2px; }
.bvl-welcome h1::after { content:''; display:block; width:44px; height:4px; border-radius:2px; background:var(--amber); margin-top:14px; }
.bvl-job { font-family:'DM Mono',ui-monospace,monospace; }
.bvl-intro { margin:16px 0 0; color:var(--text-soft); font-size:16px; line-height:1.65; max-width:640px; }
/* Everything included — a FIXED grid: 2 columns, 3 from 900px. Never auto-fit,
   so six cards are always 3+3 or 2+2+2; grid-auto-rows:1fr makes every card
   the height of the tallest. */
.bvl-incl { --gap:12px; display:grid; grid-template-columns:repeat(2, minmax(0, 1fr)); grid-auto-rows:1fr; gap:var(--gap); }
.bvl-incl-card { position:relative; background:var(--bg-tint); border:1px solid var(--line); border-radius:12px; padding:16px 16px 42px; display:flex; flex-direction:column; gap:4px; transition:border-color .15s; }
.bvl-incl-active { border-color:var(--amber); }
/* The "i": bottom-right; the card's bottom padding keeps it clear of the text. */
.bvl-incl-info { position:absolute; right:10px; bottom:10px; width:24px; height:24px; padding:0; border-radius:50%; border:1.5px solid var(--amber-deep); background:var(--white); color:var(--amber-deep); font:italic 700 13px/1 Georgia,'Times New Roman',serif; cursor:pointer; display:flex; align-items:center; justify-content:center; transition:background .15s, color .15s; }
.bvl-incl-info:hover { background:#fdf0d9; }
.bvl-incl-info:focus-visible { outline:2px solid var(--navy); outline-offset:2px; }
.bvl-incl-active .bvl-incl-info { background:var(--amber); border-color:var(--amber); color:var(--white); }
/* The panel: a full-width grid row under the open card's row. The caret sits
   over the card's centre: column width × (col + ½) plus the gaps before it. */
.bvl-incl-panel { grid-column:1 / -1; position:relative; background:#fdf6e9; border:1px solid #f3e2bf; border-radius:10px; padding:13px 16px; font-size:14px; line-height:1.55; color:var(--text); animation:bvl-incl-in .16s ease-out; }
.bvl-incl-panel::before { content:''; position:absolute; top:-7px; width:12px; height:12px; background:#fdf6e9; border-top:1px solid #f3e2bf; border-left:1px solid #f3e2bf; transform:translateX(-50%) rotate(45deg); left:calc((100% - (var(--cols) - 1) * var(--gap)) / var(--cols) * (var(--col) + 0.5) + var(--col) * var(--gap)); transition:left .18s ease; }
@keyframes bvl-incl-in { from { opacity:0; transform:translateY(-4px); } to { opacity:1; transform:none; } }
@media (prefers-reduced-motion:reduce) { .bvl-incl-panel { animation:none; } .bvl-incl-panel::before { transition:none; } }
.bvl-incl-icon { width:40px; height:40px; border-radius:10px; background:#fdf0d9; color:var(--amber-deep); display:flex; align-items:center; justify-content:center; margin-bottom:6px; flex-shrink:0; }
.bvl-incl-title { font-weight:700; color:var(--navy); font-size:15px; line-height:1.3; }
.bvl-incl-line { font-size:14px; color:var(--text-soft); line-height:1.45; }
/* Trust items — mini cards under the cards: light border, faint tint, one
   line. Inside the box: one per line, full width; from 900px the cards' own
   3-column grid and gap, each box spanning its column. The box's border +
   padding equal a card's (17px; 13px on phones), so the check sits under the
   card's icon. */
.bvl-trust-item { display:flex; align-items:center; gap:7px; font-size:13px; font-weight:500; color:var(--text-soft); line-height:1.35; background:#fafbfc; border:1px solid var(--line); border-radius:9px; padding:8px 16px; }
.bvl-trust-item svg { color:#1a7a3f; flex-shrink:0; }
.bvl-trust-grid { display:grid; grid-template-columns:1fr; gap:8px; margin-top:12px; }
.bvl-gtk-list { margin:0; padding-left:18px; font-size:14px; color:var(--text-soft); }
.bvl-gtk-list li { margin:2px 0; }
/* Header: phone and job number, side by side, same weight. */
.bvl-hdr-line { display:flex; align-items:baseline; justify-content:flex-end; gap:14px; flex-wrap:wrap; margin-top:2px; }
.bvl-hdr-line .bvl-phone { margin-top:0; }
.bvl-hdr-job { color:var(--white); font-size:14px; font-weight:700; white-space:nowrap; }
/* The consultant strip under the welcome. */
.bvl-contact { margin-top:18px; max-width:640px; background:#fdf6e9; border:1px solid #f3e2bf; border-radius:10px; padding:11px 14px; font-size:14px; line-height:1.5; color:var(--text); }
.bvl-contact b { color:var(--navy); font-weight:700; }
.bvl-contact a { color:var(--navy); font-weight:600; text-decoration:none; border-bottom:1px solid #e8c88a; white-space:nowrap; }
.bvl-contact-b { color:var(--text-soft); }
/* Name and phone stay together; the line breaks before them if it must. */
.bvl-nowrap { white-space:nowrap; }
/* Good to know — lighter than a card of rules: no accent bars. Always open. */
.bvl-card.bvl-gtk { padding:0; }
.bvl-gtk-title { margin:0; padding:18px 22px; font-weight:700; color:var(--navy); font-size:15px; }
.bvl-gtk-body { padding:0 22px 18px; display:grid; gap:14px; }
.bvl-gtk-h { font-weight:600; color:var(--text); font-size:14px; margin-bottom:2px; }
.bvl-gtk-item p { margin:0; font-size:14px; color:var(--text-soft); }
.bvl-deposit-card { background:#fffaf0; border-color:#f3e2bf; }
.bvl-card { background:var(--white); border:1px solid var(--line); border-radius:12px; padding:22px 22px; margin:16px 0; box-shadow:0 1px 3px rgba(22,35,61,0.04); }
.bvl-section-h { font-size:12px; text-transform:uppercase; letter-spacing:1.2px; color:var(--amber-deep); margin:0 0 16px; font-weight:800; }
.bvl-term { border-left:3px solid var(--amber); padding:2px 0 2px 14px; }
.bvl-term-h { font-weight:700; color:var(--navy); font-size:14px; margin-bottom:3px; }
.bvl-term p { margin:0; font-size:14px; color:var(--text-soft); }
.bvl-grid2 { display:grid; grid-template-columns:1fr 1fr; gap:18px; margin-bottom:18px; }
.bvl-grid2:last-child { margin-bottom:0; }
.bvl-label { font-size:11px; text-transform:uppercase; letter-spacing:.5px; color:var(--text-soft); margin-bottom:4px; font-weight:700; }
.bvl-value { font-size:14px; color:var(--text); }
.bvl-soft { color:var(--text-soft); }
.bvl-tiny { font-size:12px; }
.bvl-badge { display:inline-block; font-size:11px; font-weight:700; padding:2px 10px; border-radius:999px; background:#eef1f6; color:var(--navy); margin-top:4px; }
.bvl-badge-amber { background:#fdf0d9; color:var(--amber-deep); }
.bvl-weights { display:flex; gap:14px; margin-top:6px; flex-wrap:wrap; }
.bvl-weight-cell { flex:1; min-width:130px; background:var(--bg-tint); border:1px solid var(--line); border-radius:10px; padding:14px 16px; text-align:center; }
.bvl-weight-num { font-size:20px; font-weight:800; color:var(--navy); }
.bvl-weight-lbl { font-size:12px; color:var(--text-soft); margin-top:2px; text-transform:uppercase; letter-spacing:.5px; }
.bvl-weight-note { margin:10px 2px 0; font-size:13px; }
.bvl-table-wrap { overflow-x:auto; }
.bvl-lines { display:none; }
.bvl-line { padding:12px 0; border-bottom:1px solid var(--line); }
.bvl-line:first-child { padding-top:0; }
.bvl-line-top { display:flex; justify-content:space-between; align-items:baseline; gap:12px; }
.bvl-line-desc { flex:1; min-width:0; font-size:15px; color:var(--text); overflow-wrap:anywhere; }
.bvl-line-amt { flex-shrink:0; white-space:nowrap; font-weight:700; color:var(--navy); font-family:'DM Mono',ui-monospace,monospace; font-size:15px; }
.bvl-line-detail { font-size:13px; color:var(--text-soft); margin-top:2px; overflow-wrap:anywhere; }
/* Long unbroken strings (emails, addresses, notes, URLs) wrap rather than push the page wide. */
.bvl-value, .bvl-footer, .bvl-intro, .bvl-gtk-item p, .bvl-term p { overflow-wrap:anywhere; }
.bvl-table { width:100%; border-collapse:collapse; font-size:14px; }
.bvl-table th { text-align:left; font-size:11px; text-transform:uppercase; letter-spacing:.5px; color:var(--text-soft); border-bottom:2px solid var(--line); padding:9px 8px; }
.bvl-table td { padding:11px 8px; border-bottom:1px solid var(--bg-tint); }
.bvl-num { text-align:right; font-family:'DM Mono',ui-monospace,monospace; white-space:nowrap; }
.bvl-totals { margin-left:auto; max-width:340px; margin-top:16px; }
.bvl-total-row { display:flex; justify-content:space-between; padding:6px 0; font-size:14px; }
.bvl-total-row span:last-child { font-family:'DM Mono',ui-monospace,monospace; }
.bvl-grand { border-top:2px solid var(--navy); margin-top:6px; padding-top:12px; font-size:19px; font-weight:800; color:var(--navy); }
.bvl-notes { margin-top:18px; padding-top:16px; border-top:1px solid var(--line); }
.bvl-actions { display:flex; flex-direction:column; gap:12px; margin:22px 0; }
.bvl-pdf-error { font-size:13px; color:#b42318; background:#fdecea; border:1px solid #f4c7c3; border-radius:8px; padding:9px 11px; line-height:1.5; }
.bvl-btn { display:inline-flex; flex-direction:column; align-items:center; justify-content:center; gap:2px; padding:14px 20px; border-radius:10px; font-size:15px; font-weight:700; cursor:pointer; border:1px solid transparent; text-decoration:none; text-align:center; transition:filter .12s, background .12s; }
.bvl-btn:disabled { opacity:.6; cursor:default; }
.bvl-btn-lg { font-size:17px; padding:17px 24px; }
.bvl-btn-amber { background:var(--amber); color:#3a2a06; border-color:var(--amber-deep); box-shadow:0 2px 8px rgba(245,166,35,0.35); }
.bvl-btn-amber:hover:not(:disabled) { filter:brightness(1.03); }
.bvl-btn-ghost { background:var(--white); color:var(--navy); border-color:var(--line); }
.bvl-btn-ghost:hover { background:var(--bg-tint); }
.bvl-btn-reviews { background:var(--navy); color:var(--white); }
.bvl-btn-reviews:hover { background:var(--navy-deep); }
.bvl-stars { color:var(--amber); font-size:16px; letter-spacing:2px; }
.bvl-soon { font-size:11px; font-weight:600; color:var(--text-soft); }
.bvl-btn-reviews .bvl-tiny { color:#c3ccdb; }
.bvl-accepted { text-align:center; padding:16px; border-radius:10px; background:#e7f6ec; border:1px solid #b7e2c4; color:#1a7a3f; font-weight:800; font-size:17px; }
.bvl-footer { text-align:center; color:var(--text-soft); font-size:13px; margin-top:28px; padding-top:20px; border-top:1px solid var(--line); }
.bvl-footer a { color:var(--text-soft); text-decoration:none; }
.bvl-footer div { margin:2px 0; }
.bvl-modal-overlay { position:fixed; inset:0; background:rgba(22,35,61,0.55); display:flex; align-items:center; justify-content:center; padding:20px; z-index:100; }
.bvl-modal { background:var(--white); border-radius:14px; padding:30px 26px; max-width:400px; text-align:center; box-shadow:0 12px 40px rgba(0,0,0,0.25); }
.bvl-modal-check { width:56px; height:56px; margin:0 auto 14px; border-radius:50%; background:#e7f6ec; color:#1a7a3f; font-size:30px; display:flex; align-items:center; justify-content:center; font-weight:800; }
.bvl-modal h3 { margin:0 0 10px; color:var(--navy); font-size:20px; }
.bvl-modal p { margin:0 0 20px; color:var(--text-soft); font-size:15px; }
@media (min-width:900px) {
  .bvl-incl { grid-template-columns:repeat(3, minmax(0, 1fr)); }
  .bvl-trust-grid { grid-template-columns:repeat(3, minmax(0, 1fr)); column-gap:12px; }
}
@media (max-width:600px) {
  .bvl-portal:not(.bvl-print) .bvl-table-wrap { display:none; }
  .bvl-portal:not(.bvl-print) .bvl-lines { display:block; }
  .bvl-totals { max-width:none; }
  .bvl-total-row { gap:12px; }
  .bvl-total-row span:last-child { white-space:nowrap; }
  .bvl-grid2 { grid-template-columns:1fr; gap:14px; }
  .bvl-welcome { padding-top:22px; }
  .bvl-welcome h1 { font-size:24px; }
  .bvl-intro { font-size:15px; }
  .bvl-incl { --gap:10px; }
  .bvl-incl-card { padding:13px 12px 40px; }
  .bvl-incl-panel { padding:12px 13px; font-size:13.5px; }
  .bvl-trust-grid .bvl-trust-item { padding:8px 12px; }
  .bvl-contact { font-size:13px; padding:10px 12px; }
  .bvl-contact-b { display:block; }
  .bvl-contact-sep { display:none; }
  .bvl-header-right { width:100%; }
  .bvl-incl-icon { width:34px; height:34px; }
  .bvl-incl-title { font-size:14px; }
  .bvl-incl-line { font-size:13px; }
  .bvl-gtk summary { padding:16px; }
  .bvl-gtk-body { padding:0 16px 16px; }
  .bvl-card { padding:18px 16px; }
  .bvl-header-inner { padding:14px 16px; }
  .bvl-header-right { text-align:right; }
}

/* ---- THE PDF (print=1, .bvl-print) ------------------------------------------
   Plain and short: white page, no tinted cards, minimal rules, tighter type.
   Rendered by the server on Letter with 20mm margins (lib/pdf.ts). Sections, table
   rows and the totals never split across a page. Comes last so it wins over
   the narrow-screen rules above. */
/* The app's body is the CRM's dark ground; with backgrounds printed it filled
   every page below the content. The page itself is white in the PDF. */
html:has(.bvl-print), body:has(.bvl-print) { background:#fff !important; }
@media print { html, body { background:#fff !important; } }
.bvl-print { background:#fff; line-height:1.4; min-height:0; }
.bvl-print .bvl-header { background:none; color:var(--navy); }
.bvl-print .bvl-header-inner { max-width:none; padding:0 0 8px; border-bottom:2px solid var(--navy); flex-wrap:nowrap; }
.bvl-print .bvl-logo { height:34px; }
.bvl-print .bvl-tagline { display:none; }
.bvl-print-hdr { text-align:right; }
.bvl-print-job { font-size:18px; font-weight:800; color:var(--navy); line-height:1.2; }
.bvl-print-contact { font-size:11px; color:var(--text); margin-top:2px; }
.bvl-print .bvl-phone { color:var(--text); font-size:12px; margin:0; }
.bvl-print .bvl-main { max-width:none; padding:0; }
.bvl-print .bvl-card { background:none; border:0; border-radius:0; box-shadow:none; padding:0; margin:14px 0 0; break-inside:avoid; page-break-inside:avoid; }
.bvl-print .bvl-section-h { color:var(--navy); font-size:11px; margin:0 0 6px; padding-bottom:3px; border-bottom:1px solid var(--line); }
.bvl-print .bvl-grid2 { grid-template-columns:1fr 1fr; gap:4px 18px; margin-bottom:6px; }
.bvl-print .bvl-label { font-size:9.5px; margin-bottom:1px; }
.bvl-print .bvl-value { font-size:11.5px; }
.bvl-print .bvl-badge { font-size:9.5px; padding:0 6px; margin-top:2px; }
.bvl-print .bvl-weights { gap:18px; margin-top:4px; }
.bvl-print .bvl-weight-cell { flex:0 0 auto; min-width:0; background:none; border:0; padding:0; text-align:left; }
.bvl-print .bvl-weight-num { font-size:13px; }
.bvl-print .bvl-weight-lbl { font-size:9.5px; margin:0; }
.bvl-print .bvl-weight-note { font-size:10px; margin:4px 0 0; }
.bvl-print .bvl-table-wrap { overflow:visible; }
.bvl-print .bvl-table { font-size:11px; }
.bvl-print .bvl-table th { font-size:9.5px; padding:4px 6px; border-bottom:1px solid var(--text-soft); }
.bvl-print .bvl-table td { padding:4px 6px; border-bottom:1px solid var(--line); }
.bvl-print .bvl-table th.bvl-num { text-align:right; }
.bvl-print .bvl-table tr { break-inside:avoid; page-break-inside:avoid; }
.bvl-print .bvl-table thead { display:table-header-group; }
.bvl-print .bvl-totals { margin-top:6px; max-width:280px; break-inside:avoid; page-break-inside:avoid; }
.bvl-print .bvl-total-row { padding:2px 0; font-size:11.5px; }
.bvl-print .bvl-grand { font-size:14px; margin-top:4px; padding-top:5px; border-top-width:1.5px; }
.bvl-print .bvl-notes { margin-top:8px; padding-top:6px; break-inside:avoid; page-break-inside:avoid; }
.bvl-print .bvl-print-terms p { margin:0 0 3px; font-size:9.5px; line-height:1.4; color:var(--text-soft); }
.bvl-print .bvl-print-terms b { color:var(--text); font-weight:600; }
.bvl-print .bvl-print-bullets { margin:0 0 3px; padding-left:14px; font-size:9.5px; line-height:1.4; color:var(--text-soft); }
.bvl-print .bvl-footer { margin-top:14px; padding-top:6px; font-size:9.5px; break-inside:avoid; page-break-inside:avoid; }
`;
